// InviteModal.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { getUserWithEmail } from 'services/users/UserService';
import { subscribeToWorkspaceMembers } from 'services/workspaces/WorkspaceMemberService';
import {
    createWorkspaceInvites,
    listWorkspaceInvites,
    revokeWorkspaceInvite,
} from 'services/workspaces/WorkspaceInviteService';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';
import useWorkspaceAccess from 'hooks/useWorkspaceAccess';

// CSS
import './InviteModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

// Role constants for invite assignments and comparisons.
const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';
const ROLE_MEMBER = 'member';
const MAX_INVITES_PER_REQUEST = 25;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Converts a role value to a normalized role string, defaulting to 'member' for unrecognized values.
 *
 * @param {string} value - The input role value to normalize.
 * 
 * @returns {string} The normalized role string.
 */
function normalizeRole(value) {
    const role = String(value || '').trim().toLowerCase();
    if (role === ROLE_OWNER || role === ROLE_ADMIN || role === ROLE_MEMBER) return role;
    return ROLE_MEMBER;
}

/**
 * Normalizes and validates an invite email value.
 *
 * @param {string} value - The raw input value.
 * 
 * @returns {string} Lowercased email when valid, otherwise empty string.
 */
function normalizeInviteEmail(value) {
    const email = String(value || '').trim().toLowerCase();
    if (!email) return '';
    return EMAIL_PATTERN.test(email) ? email : '';
}

/**
 * Splits the input string into individual invite emails based on commas, trimming whitespace 
 * and filtering out empty entries.
 * 
 * @param {string} value - The raw input string containing one or more invite emails.
 * 
 * @returns {string[]} An array of cleaned invite email strings.
 */
function splitTargets(value) {
    return String(value || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
}

/**
 * InviteModal
 *
 * Renders the workspace invite modal with invite creation and pending invite management.
 *
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {function} setOpen - Function to set modal visibility.
 * @param {object} workspace - Active workspace record.
 * @param {function} onClose - Optional callback fired when modal closes.
 *
 * @returns {ReactNode} The rendered component.
 */
const InviteModal = React.memo(({
    isOpen,
    setOpen,
    workspace,
    onClose,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Workspace Access Context
    const { workspaceRole, can } = useWorkspaceAccess(workspace);

    // ----- STATE ----- //

    // Current space members.
    const [members, setMembers] = useState([]);

    // Invite target input.
    const [inviteInput, setInviteInput] = useState('');

    // Selected role for invite payloads.
    const [inviteRole, setInviteRole] = useState(ROLE_MEMBER);

    // Optional invite message.
    const [inviteMessage, setInviteMessage] = useState('');

    // Invite action in-flight state.
    const [inviteBusy, setInviteBusy] = useState(false);

    // Error feedback text.
    const [errorMessage, setErrorMessage] = useState('');

    // Success/status feedback text.
    const [statusMessage, setStatusMessage] = useState('');

    // Pending workspace invites list.
    const [pendingInvites, setPendingInvites] = useState([]);

    // ----- UTILITIES ----- //

    // Access flags for invite controls.
    const canInvite = can('invite.create');
    const canListInvites = can('invite.list');
    const canRevokeInvites = can('invite.revoke');

    // ----- HOOKS ----- //

    // Mobile breakpoint detection for modal sizing.
    const isMobile = useIsMobile();

    // Pending invites loader for modal actions.
    const loadPendingInvites = useCallback(async () => {
        if (!workspace?.key || !canInvite || !canListInvites) {
            setPendingInvites([]);
            return;
        }

        try {
            const rows = await listWorkspaceInvites({ workspaceKey: workspace.key });
            setPendingInvites(Array.isArray(rows) ? rows : []);
        } catch (error) {
            setPendingInvites([]);
            setErrorMessage(error?.message || 'Failed to load pending invites.');
        }
    }, [workspace?.key, canInvite, canListInvites]);

    // ----- EFFECTS ----- //

    // Subscribe to current space members while the modal is open.
    useEffect(() => {
        if (!isOpen || !workspace?.key) {
            setMembers([]);
            return;
        }

        const unsubscribe = subscribeToWorkspaceMembers(
            workspace.key,
            (rows) => setMembers(Array.isArray(rows) ? rows : []),
            (error) => setErrorMessage(error?.message || 'Failed to load members.')
        );

        return () => {
            unsubscribe && unsubscribe();
        };
    }, [isOpen, workspace?.key]);

    // Load pending invites when modal and access state allow it.
    useEffect(() => {
        if (!isOpen || !workspace?.key || !canInvite) {
            setPendingInvites([]);
            return;
        }

        loadPendingInvites();
    }, [isOpen, workspace?.key, canInvite, loadPendingInvites]);

    // ----- HANDLERS ----- //

    /**
     * Resets modal invite state and closes the modal.
     */
    const handleClose = () => {
        setInviteBusy(false);
        setErrorMessage('');
        setStatusMessage('');
        setInviteInput('');
        setInviteRole(ROLE_MEMBER);
        setInviteMessage('');
        if (setOpen) setOpen(false);
        if (onClose) onClose();
    };

    /**
     * Builds and sends workspace invites for entered targets.
     */
    const handleInvite = async () => {
        if (!workspace?.key || !canInvite || inviteBusy) return;

        const targets = splitTargets(inviteInput);
        if (!targets.length) {
            setErrorMessage('Enter at least one email address.');
            return;
        }

        if (targets.length > MAX_INVITES_PER_REQUEST) {
            setErrorMessage(`You can invite up to ${MAX_INVITES_PER_REQUEST} people at once.`);
            return;
        }

        setInviteBusy(true);
        setErrorMessage('');
        setStatusMessage('');

        try {
            const existingKeys = new Set(members.map((member) => member.userKey || member.key));
            const roleToAssign = workspaceRole === ROLE_OWNER ? normalizeRole(inviteRole) : ROLE_MEMBER;

            let prepared = 0;
            const invalidEmails = [];
            const skipped = [];
            const invites = [];
            const seenTargets = new Set();

            const appendInvite = (payload, dedupeKey, rawTarget) => {
                if (seenTargets.has(dedupeKey)) {
                    skipped.push(rawTarget);
                    return;
                }
                seenTargets.add(dedupeKey);
                invites.push({
                    ...payload,
                    role: roleToAssign,
                    message: inviteMessage,
                });
                prepared += 1;
            };

            for (const rawTarget of targets) {
                const email = normalizeInviteEmail(rawTarget);
                if (!email) {
                    invalidEmails.push(rawTarget);
                    continue;
                }

                const existingUser = await getUserWithEmail(email);

                if (existingUser?.key && existingKeys.has(existingUser.key)) {
                    skipped.push(rawTarget);
                    continue;
                }

                appendInvite(
                    { email, userKey: existingUser?.key || null },
                    existingUser?.key ? `u:${existingUser.key}` : `e:${email}`,
                    rawTarget
                );

                if (existingUser?.key) {
                    existingKeys.add(existingUser.key);
                }
            }

            if (!invites.length && invalidEmails.length && !skipped.length) {
                setErrorMessage('Enter at least one valid email address.');
                return;
            }

            let createdCount = 0;
            let serverSkippedCount = 0;
            let deliverySentCount = 0;
            let deliveryFailedCount = 0;

            if (invites.length) {
                const response = await createWorkspaceInvites({
                    workspaceKey: workspace.key,
                    invites,
                    role: roleToAssign,
                    message: inviteMessage,
                });

                createdCount = Number(response?.createdCount || 0);
                serverSkippedCount = Number(response?.skippedCount || 0);
                deliverySentCount = Number(response?.deliverySentCount || 0);
                deliveryFailedCount = Number(response?.deliveryFailedCount || 0);
                await loadPendingInvites();
            }

            if (!createdCount && !invalidEmails.length && (skipped.length || serverSkippedCount)) {
                setStatusMessage('All selected emails are already members.');
                return;
            }

            const messageParts = [];
            if (deliverySentCount) messageParts.push(`Sent ${deliverySentCount} invite email${deliverySentCount === 1 ? '' : 's'}.`);
            if (createdCount && !deliverySentCount) messageParts.push(`Created ${createdCount} invite${createdCount === 1 ? '' : 's'}.`);
            if (deliveryFailedCount) messageParts.push(`${deliveryFailedCount} email${deliveryFailedCount === 1 ? '' : 's'} failed delivery.`);
            if (prepared && !createdCount && !serverSkippedCount) messageParts.push(`Prepared ${prepared} invite target${prepared === 1 ? '' : 's'}.`);
            if (skipped.length) messageParts.push(`${skipped.length} already existed.`);
            if (serverSkippedCount) messageParts.push(`${serverSkippedCount} skipped on server.`);
            if (invalidEmails.length) messageParts.push(`${invalidEmails.length} invalid email${invalidEmails.length === 1 ? '' : 's'}.`);

            setStatusMessage(messageParts.join(' '));
            if (createdCount > 0) {
                setInviteInput('');
                setInviteRole(ROLE_MEMBER);
                setInviteMessage('');
            }
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to send invite.');
        } finally {
            setInviteBusy(false);
        }
    };

    /**
     * Revokes a pending workspace invite.
     *
     * @param {string} inviteKey - Invite key to revoke.
     */
    const handleRevokeInvite = async (inviteKey) => {
        if (!workspace?.key || !inviteKey || inviteBusy || !canRevokeInvites) return;

        setInviteBusy(true);
        setErrorMessage('');

        try {
            await revokeWorkspaceInvite({ workspaceKey: workspace.key, inviteKey });
            setStatusMessage('Invite revoked.');
            await loadPendingInvites();
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to revoke invite.');
        } finally {
            setInviteBusy(false);
        }
    };

    // ----- RENDER ----- //

    return (

        <Modal
            isOpen={isOpen}
            height="auto"
            width={isMobile ? '100%' : '460px'}
            iconKey="person_add"
            title="Invite Members"
            onClose={handleClose}
        >
            <div className="invite-modal-container">

                {/* INVITE TARGETS */}
                <div className="invite-modal-section">

                    <ModalLabel label="Email Addresses" />
                    <TextInput
                        disabled={!canInvite || inviteBusy}
                        height="40px"
                        value={inviteInput}
                        placeholder="name@company.com, teammate@company.com"
                        onChange={(event) => setInviteInput(event.target.value)}
                        onEnterPress={handleInvite}
                    />
                    <div
                        className="invite-modal-hint"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Separate multiple entries with commas. Max {MAX_INVITES_PER_REQUEST} invites per send.
                    </div>
                </div>

                {/* ROLE */}
                <div className="invite-modal-section invite-modal-role-row">
                    <div className="invite-modal-role-left">

                        <ModalLabel label="Role" />
                        <div
                            className="invite-modal-role-copy"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            {workspaceRole === ROLE_OWNER
                                ? 'Admins can manage members and settings.'
                                : 'As an admin, invites are always sent as member.'}
                        </div>
                    </div>

                    <SelectInput
                        style={{
                            height: 40,
                            minWidth: 124,
                        }}
                        value={workspaceRole === ROLE_OWNER ? inviteRole : ROLE_MEMBER}
                        disabled={inviteBusy || workspaceRole !== ROLE_OWNER}
                        onChange={(event) => setInviteRole(normalizeRole(event.target.value))}
                    >
                        <option value={ROLE_MEMBER}>member</option>
                        <option value={ROLE_ADMIN}>admin</option>
                    </SelectInput>
                </div>

                {/* MESSAGE */}
                <div className="invite-modal-section">

                    <ModalLabel label=" Message (optional)" />
                    <TextInput
                        style={{
                            maxHeight: 120,
                            minHeight: 84,
                        }}
                        multiline
                        expandable
                        maxLength={280}
                        value={inviteMessage}
                        placeholder="Add a note to your invite"
                        disabled={!canInvite || inviteBusy}
                        onChange={(event) => setInviteMessage(event.target.value)}
                    />
                </div>

                {/* ACCESS NOTE */}
                {!canInvite && (
                    <div
                        className="invite-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Only workspace owners and admins can invite members.
                    </div>
                )}

                {/* FEEDBACK */}
                {errorMessage && <div className="invite-modal-error">{errorMessage}</div>}
                {statusMessage && !errorMessage &&
                    <div className="invite-modal-status"
                        style={{
                            color: theme.success,
                        }}>
                        {statusMessage}
                    </div>
                }

                {/* PENDING INVITES */}
                {pendingInvites.length > 0 && (
                    <div className="invite-modal-section">
                        <div
                            className="invite-modal-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Pending invites
                        </div>
                        <div className="invite-modal-members-list">
                            {pendingInvites.map((invite) => {
                                const key = invite.key;
                                const target = invite.invitedEmail || invite.invitedUserKey || key;

                                return (
                                    <div
                                        key={key}
                                        className="invite-modal-member-row"
                                        style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        <div className="invite-modal-member-meta">
                                            <div className="invite-modal-member-name">{target}</div>
                                            <div className="invite-modal-member-sub">
                                                {invite.role} • {invite.deliveryStatus || 'queued'}
                                            </div>
                                            {invite.deliveryStatus === 'failed' && invite.deliveryLastError && (
                                                <div className="invite-modal-member-sub invite-modal-member-sub-error">
                                                    {invite.deliveryLastError}
                                                </div>
                                            )}
                                        </div>

                                        <div className="invite-modal-member-actions">
                                            <Button
                                                label="Revoke"
                                                enabled={!inviteBusy && canRevokeInvites}
                                                onClick={() => handleRevokeInvite(key)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ACTIONS */}
                <div className="invite-modal-actions">
                    <Button
                        label="Cancel"
                        enabled={!inviteBusy}
                        onClick={handleClose}
                    />
                    <Button
                        iconKey="send"
                        label={inviteBusy ? 'Sending...' : 'Send invite'}
                        enabled={canInvite && !inviteBusy}
                        onClick={handleInvite}
                    />
                </div>
            </div>
        </Modal>
    );

});

// Dev Tools
InviteModal.displayName = 'InviteModal';

export default InviteModal;

